const systems = [
  {
    name: "Split System",
    slug: "split-system",
    bestFor: "Single rooms, bedrooms, home offices",
    typical: "One indoor head, one outdoor unit",
    heating: "Good",
    notes: "Cost-effective way to heat and cool one or two rooms. A strong entry point for a single Highlands bedroom or living area, but not efficient as your only heating for a whole home.",
  },
  {
    name: "Multi-Head Split (MultiAir)",
    slug: "multiair-system",
    bestFor: "2–5 separate rooms off one outdoor unit",
    typical: "One outdoor condenser, multiple indoor heads",
    heating: "Good–Very Good",
    notes: "Useful where you want independent control of a few rooms without running full ductwork, though heating capacity per head is shared from one outdoor unit.",
  },
  {
    name: "Ducted Reverse Cycle",
    slug: "ducted-system",
    bestFor: "Whole-home heating and cooling with zoning",
    typical: "Central unit, ceiling or under-floor ducting to every room",
    heating: "Excellent",
    notes: "Our most common recommendation for larger Highlands homes. Zoning lets you heat only the rooms in use, which matters a lot given how many heating hours a Highlands winter demands.",
  },
  {
    name: "Gas Ducted Heating",
    slug: "gas-ducted-heating-systems",
    bestFor: "Whole-home heating where gas is available",
    typical: "Central gas furnace, ducted throughout the home",
    heating: "Excellent",
    notes: "Delivers strong, fast heat output and can suit larger or older homes with higher heat loss. Doesn't provide cooling, so it's usually paired with a separate cooling solution.",
  },
  {
    name: "Floor Heating",
    slug: "floor-heating-system",
    bestFor: "Heritage homes, bathrooms, renovations",
    typical: "Hydronic or electric elements under the floor",
    heating: "Very Good",
    notes: "Even, radiant warmth that suits stone and timber-floored heritage cottages common in Berrima and Bowral, without changing the look of period rooms.",
  },
  {
    name: "Ceiling Cassette",
    slug: "ceiling-cassette",
    bestFor: "Open-plan living areas, renovations, commercial fitouts",
    typical: "Discreet ceiling-mounted unit with 360° airflow",
    heating: "Good",
    notes: "Keeps walls clear and distributes air evenly around a room — a tidy option where you don't want a wall-mounted head on show.",
  },
  {
    name: "VRV / VRF System",
    slug: "vrv-system",
    bestFor: "Large homes, multi-storey properties, commercial buildings",
    typical: "Single outdoor system serving many indoor zones",
    heating: "Excellent",
    notes: "Handles simultaneous heating and cooling in different zones of a large property — a good fit for sprawling Highlands homesteads or mixed-use buildings.",
  },
  {
    name: "Evaporative Cooling",
    slug: "evaporative-cooling",
    bestFor: "Summer cooling in drier inland areas (e.g. Goulburn)",
    typical: "Roof-mounted unit drawing fresh, cooled air through the home",
    heating: "None",
    notes: "Effective and cheap to run for cooling, but provides no heating — in the Highlands it needs to be paired with a dedicated heating system for winter.",
  },
];

const HomeChoosingSystem = () => {
  return (
    <section className="py-16 md:py-20 px-4 bg-muted">
      <div className="container mx-auto max-w-5xl">
        <h2 className="text-3xl md:text-4xl font-black text-foreground mb-4 uppercase tracking-wide">
          How to Choose the Right System
        </h2>
        <p className="text-muted-foreground text-lg mb-8 max-w-3xl">
          There's no single "best" air conditioner — the right choice depends on your home's layout,
          how many rooms you need to heat, your budget and how the property was built. Here's how the
          main system types compare for Southern Highlands conditions, with links through to a full
          breakdown of each.
        </p>

        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm mb-8">
          <table className="w-full text-sm md:text-base">
            <thead>
              <tr className="bg-secondary text-secondary-foreground text-left">
                <th className="px-4 py-3 font-bold">System</th>
                <th className="px-4 py-3 font-bold">Best For</th>
                <th className="px-4 py-3 font-bold hidden md:table-cell">Typical Setup</th>
                <th className="px-4 py-3 font-bold">Heating Strength</th>
              </tr>
            </thead>
            <tbody>
              {systems.map((s, i) => (
                <tr key={s.slug} className={i % 2 === 0 ? "bg-card" : "bg-muted/50"}>
                  <td className="px-4 py-3 font-semibold text-foreground align-top">
                    <a href={`/ac-types/${s.slug}`} className="text-primary hover:underline">
                      {s.name}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground align-top">{s.bestFor}</td>
                  <td className="px-4 py-3 text-muted-foreground align-top hidden md:table-cell">{s.typical}</td>
                  <td className="px-4 py-3 text-muted-foreground align-top">{s.heating}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {systems.map((s) => (
            <div key={s.slug} className="bg-card border border-border rounded-xl p-5 shadow-sm">
              <h3 className="font-bold text-foreground mb-1">
                <a href={`/ac-types/${s.slug}`} className="hover:text-primary transition-colors">
                  {s.name}
                </a>
              </h3>
              <p className="text-muted-foreground text-sm">{s.notes}</p>
            </div>
          ))}
        </div>

        <p className="text-muted-foreground mt-8">
          Not sure which of these fits your home? Browse our{" "}
          <a href="/ac-types" className="text-primary hover:underline font-semibold">full AC types guide</a>,
          compare trusted{" "}
          <a href="/brands" className="text-primary hover:underline font-semibold">brands we install</a>, or
          book a free on-site assessment via{" "}
          <a href="/schedule-quote" className="text-primary hover:underline font-semibold">schedule a quote</a>.
          Most installations are also available with{" "}
          <a href="/finance-options" className="text-primary hover:underline font-semibold">interest-free finance</a>.
        </p>
      </div>
    </section>
  );
};

export default HomeChoosingSystem;
